













































































































import { Component, Prop, Vue } from "vue-property-decorator";
import rest from "@/rest";
// components
import SimpleMessageBox from "@/components/Shared/SimpleMessageBox.vue";
import TrainingAppointmentEditDialog from "@/components/Academy/TrainingAppointmentEditDialog.vue";
import TrainingLocationsDialog from "@/components/Academy/TrainingLocationsDialog.vue";
// interfaces
import { ParamDictionary } from "@/interfaces/ParamDictionary";
import { TrainingAppointmentVm, TrainingAppointmentStatus } from "@/interfaces/TrainingAppointmentVm";
import { TrainingLocationVm } from "@/interfaces/TrainingLocationVm";

// Appointments of an event. Emits "changed" after every change (the counts in the event header are outdated then).
@Component({
  components: {
    SimpleMessageBox,
    TrainingAppointmentEditDialog,
    TrainingLocationsDialog
  }
})
export default class TrainingAppointmentsPanel extends Vue {
  @Prop({ required: true, type: Number }) readonly eventId!: number;
  @Prop({ required: true, type: Number }) readonly orgId!: number;
  @Prop({ type: String, default: "" }) readonly orgName!: string;
  // Training leads may change appointments, persons who are only responsible may look at their appointments
  @Prop({ type: Boolean, default: false }) readonly canEdit!: boolean;
  // false for archived and cancelled events
  @Prop({ type: Boolean, default: true }) readonly editable!: boolean;

  readonly TrainingAppointmentStatus = TrainingAppointmentStatus;

  appointments: TrainingAppointmentVm[] = [];
  locations: TrainingLocationVm[] = [];
  loading = false;

  showEditDialog = false;
  editedAppointment: TrainingAppointmentVm | null = null;
  showLocationsDialog = false;

  showCancelDialog = false;
  showDeleteDialog = false;
  selectedAppointment: TrainingAppointmentVm | null = null;

  async mounted(): Promise<void> {
    await Promise.all([this.loadAppointments(), this.loadLocations()]);
  }

  get cancelText(): string {
    const a = this.selectedAppointment;
    const count = a ? a.registeredCount + a.waitlistCount : 0;
    return count > 0
      ? `Der Termin wird abgesagt und alle ${count} Anmeldungen und Wartelisten-Einträge werden storniert. Wirklich absagen?`
      : "Der Termin wird abgesagt. Wirklich absagen?";
  }

  eventParams(): ParamDictionary {
    const params: ParamDictionary = { dictionary: {} };
    params.dictionary!["EventId"] = this.eventId.toString();
    return params;
  }

  async loadAppointments(): Promise<void> {
    this.loading = true;
    try {
      // On errors the rest wrapper shows the message and returns undefined
      this.appointments = (await rest.url("trainingLead/getAppointments").post(this.eventParams())) ?? [];
    } finally {
      this.loading = false;
    }
  }

  async loadLocations(): Promise<void> {
    if (!this.canEdit) return;
    const params: ParamDictionary = { dictionary: {} };
    params.dictionary!["OrgId"] = this.orgId.toString();
    this.locations = (await rest.url("trainingLead/getLocations").post(params)) ?? [];
  }

  statusMeta(appointment: TrainingAppointmentVm): { text: string; color: string } {
    switch (appointment.status) {
      case TrainingAppointmentStatus.Cancelled:
        return { text: "Abgesagt", color: "error" };
      case TrainingAppointmentStatus.Closed:
        return { text: "Abgeschlossen", color: "grey" };
      default:
        return new Date(appointment.endDate) < new Date() ? { text: "Vergangen", color: "grey" } : { text: "Geplant", color: "success" };
    }
  }

  openEditor(appointment: TrainingAppointmentVm | null): void {
    this.editedAppointment = appointment;
    this.showEditDialog = true;
  }

  async onSaved(): Promise<void> {
    await this.loadAppointments();
    this.$emit("changed");
  }

  askCancel(appointment: TrainingAppointmentVm): void {
    this.selectedAppointment = appointment;
    this.showCancelDialog = true;
  }

  askDelete(appointment: TrainingAppointmentVm): void {
    this.selectedAppointment = appointment;
    this.showDeleteDialog = true;
  }

  appointmentParams(): ParamDictionary {
    const params: ParamDictionary = { dictionary: {} };
    params.dictionary!["AppointmentId"] = this.selectedAppointment!.id.toString();
    return params;
  }

  async cancelAppointment(): Promise<void> {
    if (!this.selectedAppointment) return;
    const result = await rest.url("trainingLead/cancelAppointment").post(this.appointmentParams());
    if (result) await this.onSaved();
  }

  async deleteAppointment(): Promise<void> {
    if (!this.selectedAppointment) return;
    // The response is an empty string on success and undefined after an error (message shown by the rest wrapper)
    const result = await rest.url("trainingLead/deleteAppointment").post(this.appointmentParams());
    if (result !== undefined) await this.onSaved();
  }
}
